import {
  IScreeningItemAvailableChildrenProperties,
  IScreeningItemAvailableOption,
  IScreeningItemDetails,
  IScreeningItemPortfolios,
  ScreeningOption,
} from '@/interfaces/portfolioOptimization.interface'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Paper,
  TextField,
  Typography,
} from '@mui/material'
import React, { useEffect, useState } from 'react'

import { NoDataToDisplayCard } from '@/components/Base/NoDataToDisplayCard/NoDataToDisplayCard'
import { ConfigSettingUI } from '@/components/PortfolioOptimization/Accordions/CandidateScreens/CandidateScreenConfigSettings'
import { ScreenCriteriaPortfolioGrid } from '@/components/PortfolioOptimization/Portfolios/ScreenCriteriaPortfolioGrid'
import { ConfigurableList } from '@/components/Shared/ConfigurableList'
import { MinMaxInput } from '@/components/Shared/MinMaxInput'
import { RadioButtons } from '@/components/Shared/RadioButtons'
import { SearchBar } from '@/components/Shared/SearchBar'

import { parseApiError } from '@/utils/parseApiError'

import { TreeViewList } from '@/components/Shared/TreeViewList'
import {
  CALLDATE_OPTIONS,
  COUPONS_OPTIONS,
  INFO_STRUCT_OPTIONS,
  MIN_MAX_DOUBLE_OPTIONS,
  MIN_MAX_FLOAT_OPTIONS,
  PUT_DATE_OPTIONS,
  REFUNDING_DATE_OPTIONS,
  USER_INPUT_OPTIONS,
} from '@/constants/portfolioOptimization.constant'
import { API_DATE_TIME_FORMAT } from '@/constants/scenarioBuilder.constant'
import {
  CriteriaOperation,
  ScreeningItemType,
} from '@/enums/portfolio-optimization.enum'
import { useAppSelector } from '@/hooks/use-app-selector'
import {
  computeScreenItemData,
  createComputeScreenItemDataPayload,
  createComputeScreenItemDataStatement,
  getCandidateScreeningItemDetails,
  isOptionMatch,
} from '@/services/portfolio-optimization/candidate-screening.service'
import { showSnackbar } from '@/states/snackbar.slice'
import { getLatestDate } from '@/utils/utils'
import { LoadingCard } from '@components/Base/LoadingCard/LoadingCard'
import dayjs from 'dayjs'
import { useDispatch } from 'react-redux'

const useStyles = {
  root: {
    display: 'flex',
    height: '70vh',
    overflow: 'hidden',
  },
  leftColumn: {
    flex: '0 0 30%',
    borderRight: '1px solid #E5E5E5',
  },
  rightColumn: {
    flex: 1,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '8px 16px',
    height: '40px',
  },
  content: {
    padding: '8px',
    height: 'calc(70vh - 40px - 32px)',
    overflowY: 'auto',
  },
  dialogActions: {
    alignItems: 'flex-end',
  },
}

type CriteriaModalProps = {
  isOpen: boolean
  mode: CriteriaOperation
  onSubmit: (rawCriteria: any, computedCriteria: string) => void
  onClose: () => void
  screeningOptions: ScreeningOption[]
  defaultScreeningOption: ScreeningOption
  setSelectedScreenCriteriaState: (selectedCriteriaOption: any) => void
  setIsNewScreenCriteriaAdded: (value: boolean) => void
}

const CriteriaModal: React.FC<CriteriaModalProps> = ({
  isOpen,
  mode,
  onSubmit,
  onClose,
  screeningOptions,
  defaultScreeningOption,
  setSelectedScreenCriteriaState,
  setIsNewScreenCriteriaAdded,
}: CriteriaModalProps) => {
  const [minTouched, setMinTouched] = useState(false);
  const [maxTouched, setMaxTouched] = useState(false);

  const dispatch = useDispatch()
  const selectedPortfolios = useAppSelector(
    (s) => s.portfolioOptimization.selectedPortfolios,
  )
  const classes = useStyles
  const COUPONS = COUPONS_OPTIONS
  const [showLoader, setShowLoader] = useState<boolean>(true)
  const [localMessage, setLocalMessage] = useState<{ type: 'error' | 'info', text: string } | null>(null);
  const [includeExcludeOption, setIncludeExcludeOption] = useState<
    'Include' | 'Exclude'
  >('Include')
  const [selectedScreeningOption, setSelectedScreeningOption] =
    useState<ScreeningOption>(defaultScreeningOption)
  const [selectedScreeningItemDetails, setSelectedScreeningItemDetails] =
    useState<IScreeningItemDetails>({
      availableOptions: {} as IScreeningItemAvailableOption,
      configOptions: {},
      itemType: '',
    })

  // Timestamp to prevent duplicate API calls within short time window
  const lastDetailsApiCallTime = React.useRef(0)
  const [selectedConfigSetting, setSelectedConfigSetting] = useState({
    couponOption: COUPONS[0],
    refundingDateOption: REFUNDING_DATE_OPTIONS[0],
    putDateOption: PUT_DATE_OPTIONS[0],
    callDateOption: CALLDATE_OPTIONS[0],
    currentWalaOption: '',
    minMaxDoubleOption: '',
    minMaxFloatOption: '',
    userInputOption: '',
  })
  const [minMaxValues, setMinMaxValues] = useState({ min: '', max: '' })
  const [criteriaValues, setCriteriaValues] = useState<string[]>([])
  const [userInputValue, setUserInputValue] = useState<string>('')
  const [userInputError, setUserInputError] = useState<boolean>(false)
  const [searchTerm, setSearchTerm] = useState<string>('')
  const [searchListTerm, setSearchListTerm] = useState<string>('')
  const [selectedTreeItems, setSelectedTreeItems] = useState<
    IScreeningItemAvailableChildrenProperties[]
  >([])

  const handleValueChange = (newValues: { min: string; max: string }) => {
    setMinMaxValues(newValues)
    let selectedOption = ''
    if (selectedScreeningOption.id === ScreeningItemType.COUP) {
      selectedOption = selectedConfigSetting.couponOption
    } else if (selectedScreeningOption.id === ScreeningItemType.FSTREFD) {
      selectedOption = selectedConfigSetting.refundingDateOption
    } else if (selectedScreeningOption.id === ScreeningItemType.PUTDATE) {
      selectedOption = selectedConfigSetting.putDateOption
    } else if (selectedScreeningOption.id === ScreeningItemType.CALLDATE) {
      selectedOption = selectedConfigSetting.callDateOption
    } else if (selectedScreeningOption.id === ScreeningItemType.CURWALA) {
      selectedOption = selectedConfigSetting.currentWalaOption
    } else if (
      isOptionMatch(selectedScreeningOption.id, MIN_MAX_DOUBLE_OPTIONS)
    ) {
      selectedOption = selectedConfigSetting.minMaxDoubleOption
    } else if (
      isOptionMatch(selectedScreeningOption.id, MIN_MAX_FLOAT_OPTIONS)
    ) {
      selectedOption = selectedConfigSetting.minMaxFloatOption
    } else if (isOptionMatch(selectedScreeningOption.id, USER_INPUT_OPTIONS)) {
      selectedOption = selectedConfigSetting.userInputOption
    }
    setSelectedScreenCriteriaState({
      selectedScreeningItem: selectedScreeningOption,
      data: {
        ...newValues,
        selectedSettingItem: selectedOption,
        isInclude: includeExcludeOption === 'Include' ? true : false,
      },
      configOptions: selectedScreeningItemDetails.configOptions,
      itemType: selectedScreeningItemDetails.itemType,
    })
  }

  const handleListSelect = (option: ScreeningOption) =>
    setSelectedScreeningOption({ ...option })

  const handlePortfolioSelect = (portfolios: IScreeningItemPortfolios[]) => {
    setCriteriaValues(portfolios.map((p) => p.shortName))
    setSelectedScreenCriteriaState({
      selectedScreeningItem: selectedScreeningOption,
      data: portfolios,
      configOptions: selectedScreeningItemDetails.configOptions,
      itemType: selectedScreeningItemDetails.itemType,
    })
  }

  const handleDialogClose = (_: any, reason: string) =>
    reason !== 'backdropClick' && onClose()

  const isConfigOptionsAvailable = () => {
    const configOptions = Object.keys(
      selectedScreeningItemDetails.configOptions,
    )
    if (
      !configOptions.length ||
      (configOptions.length === 1 &&
        selectedScreeningItemDetails.configOptions.hasIncludeExclude)
    ) {
      return false
    }
    return true
  }

  const validateSubmit = (): string => {
    let errorMsg = ''
    const { configOptions } = selectedScreeningItemDetails
    if (configOptions?.hasPortfolioGrid && criteriaValues.length === 0) {
      errorMsg = 'Please select at least one portfolio.'
    }

    if (
      selectedScreeningOption.id === ScreeningItemType.MEMCODE &&
      configOptions?.hasBenchmark &&
      !selectedTreeItems.length
    ) {
      errorMsg = 'Please select at least one benchmark'
    }

    if (
      isOptionMatch(selectedScreeningOption.id, INFO_STRUCT_OPTIONS) &&
      configOptions?.hasInvert &&
      !selectedTreeItems.length
    ) {
      errorMsg = 'Please select at least one item'
    }

    if (
      (configOptions?.hasMinMax ||
        configOptions.hasMinMaxDate ||
        configOptions.hasNoSelection ||
        configOptions.hasRefundableSecuritiesOnly ||
        configOptions.hasNonRefundableSecuritiesOnly ||
        configOptions.hasPutableSecuritiesOnly ||
        configOptions.hasNonPutableSecuritiesOnly ||
        configOptions.hasMinMaxDouble ||
        configOptions.hasMinMaxShort ||
        configOptions.hasMinMaxFloat) &&
      !minMaxValues.min &&
      !minMaxValues.max
    ) {
      errorMsg = 'Please enter either minimum or maximum value.'
    }
    
    if (
      isOptionMatch(selectedScreeningOption.id, USER_INPUT_OPTIONS) &&
      configOptions?.hasUserInput &&
      !userInputValue.length
    ) {
      errorMsg = 'Please enter input value.'
    }

    return errorMsg
  }

  const getSelectedCriteriaNames = () => {
    let titles: string[] = []
    criteriaValues.forEach((option) => {
      const criteriaItems = option.split('_')
      let criteriaName = criteriaItems[1]
      if (criteriaItems.length < 3) {
        criteriaName = criteriaItems[0]
      }
      titles.push(criteriaName)
    })

    return titles
  }

  const updateConfigState = () => {
    setSelectedScreenCriteriaState({
      selectedScreeningItem: selectedScreeningOption,
      data: {
        selectedTitles: selectedTreeItems,
        isInclude: includeExcludeOption === 'Include' ? true : false,
      },
      configOptions: selectedScreeningItemDetails.configOptions,
      itemType: selectedScreeningItemDetails.itemType,
    })
  }

  const handleSubmit = async () => {
    const error = validateSubmit()
    if (error) {
      setLocalMessage({ type: 'error', text: error });
      return;
    }
    const { configOptions, itemType } = selectedScreeningItemDetails
    const isIncluded = includeExcludeOption === 'Include'
    let criteriaStatements = {}
    if (configOptions) {
      if (configOptions.hasPortfolioGrid) {
        criteriaStatements = createComputeScreenItemDataStatement(
          selectedScreeningOption,
          criteriaValues,
          itemType,
          { isInclude: isIncluded },
        )
      } else if (
        selectedScreeningOption.id === ScreeningItemType.COUP &&
        (configOptions.hasMinMaxFloat ||
          configOptions.hasAllCoupons ||
          configOptions.hasHalfCouponsExcluded ||
          configOptions.hasHalfCouponsIncluded)
      ) {
        criteriaStatements = createComputeScreenItemDataPayload(
          selectedScreeningOption,
          minMaxValues,
          itemType,
          {
            isInclude: isIncluded,
            isAllCoupons:
              selectedConfigSetting.couponOption === COUPONS_OPTIONS[0]
                ? true
                : false,
            isHalfCouponsExcluded:
              selectedConfigSetting.couponOption === COUPONS_OPTIONS[1]
                ? true
                : false,
            isHalfCouponsIncluded:
              selectedConfigSetting.couponOption === COUPONS_OPTIONS[2]
                ? true
                : false,
          },
        )
      } else if (configOptions.hasMinMax) {
        criteriaStatements = createComputeScreenItemDataPayload(
          selectedScreeningOption,
          minMaxValues,
          itemType,
          {
            isInclude: isIncluded,
          },
        )
      } else if (
        (selectedScreeningOption.id === ScreeningItemType.MEMCODE &&
          configOptions.hasBenchmark) ||
        (isOptionMatch(selectedScreeningOption.id, INFO_STRUCT_OPTIONS) &&
          configOptions.hasInvert)
      ) {
        criteriaStatements = createComputeScreenItemDataStatement(
          selectedScreeningOption,
          selectedTreeItems,
          itemType,
          { isInclude: isIncluded },
        )
        updateConfigState()
      } else if (
        selectedScreeningOption.id === ScreeningItemType.FSTREFD &&
        (configOptions.hasMinMaxDate ||
          configOptions.hasNoSelection ||
          configOptions.hasRefundableSecuritiesOnly ||
          configOptions.hasNonRefundableSecuritiesOnly)
      ) {
        criteriaStatements = createComputeScreenItemDataPayload(
          selectedScreeningOption,
          minMaxValues,
          itemType,
          {
            isInclude: isIncluded,
            isNoSelection:
              selectedConfigSetting.refundingDateOption ===
              REFUNDING_DATE_OPTIONS[0]
                ? true
                : false,
            isRefundableSecuritiesOnly:
              selectedConfigSetting.refundingDateOption ===
              REFUNDING_DATE_OPTIONS[1]
                ? true
                : false,
            isNonRefundableSecuritiesOnly:
              selectedConfigSetting.refundingDateOption ===
              REFUNDING_DATE_OPTIONS[2]
                ? true
                : false,
          },
        )
      } else if (
        selectedScreeningOption.id === ScreeningItemType.PUTDATE &&
        (configOptions.hasMinMaxDate ||
          configOptions.hasNoSelection ||
          configOptions.hasPutableSecuritiesOnly ||
          configOptions.hasNonPutableSecuritiesOnly)
      ) {
        criteriaStatements = createComputeScreenItemDataPayload(
          selectedScreeningOption,
          minMaxValues,
          itemType,
          {
            isInclude: isIncluded,
            isNoSelection:
              selectedConfigSetting.putDateOption === PUT_DATE_OPTIONS[0]
                ? true
                : false,
            isPutableSecuritiesOnly:
              selectedConfigSetting.putDateOption === PUT_DATE_OPTIONS[1]
                ? true
                : false,
            isNonPutableSecuritiesOnly:
              selectedConfigSetting.putDateOption === PUT_DATE_OPTIONS[2]
                ? true
                : false,
          },
        )
      } else if (
        selectedScreeningOption.id === ScreeningItemType.CALLDATE &&
        (configOptions.hasMinMaxDate ||
          configOptions.hasNoSelection ||
          configOptions.hasCallable ||
          configOptions.hasCallableMakeWhole ||
          configOptions.hasCallableNonMakeWhole ||
          configOptions.hasNonCallable ||
          configOptions.hasNonCallableLife)
      ) {
        criteriaStatements = createComputeScreenItemDataPayload(
          selectedScreeningOption,
          minMaxValues,
          itemType,
          {
            isInclude: isIncluded,
            isNoSelection:
              selectedConfigSetting.callDateOption === CALLDATE_OPTIONS[0]
                ? true
                : false,
            isCallable:
              selectedConfigSetting.callDateOption === CALLDATE_OPTIONS[1]
                ? true
                : false,
            isCallableMakeWhole:
              selectedConfigSetting.callDateOption === CALLDATE_OPTIONS[2]
                ? true
                : false,
            isCallableNonMakeWhole:
              selectedConfigSetting.callDateOption === CALLDATE_OPTIONS[3]
                ? true
                : false,
            isNonCallable:
              selectedConfigSetting.callDateOption === CALLDATE_OPTIONS[4]
                ? true
                : false,
            isNonCallableLife:
              selectedConfigSetting.callDateOption === CALLDATE_OPTIONS[5]
                ? true
                : false,
          },
        )
      } else if (
        (selectedScreeningOption.id === ScreeningItemType.CURWALA &&
          configOptions.hasMinMaxShort) ||
        (isOptionMatch(selectedScreeningOption.id, MIN_MAX_DOUBLE_OPTIONS) &&
          configOptions.hasMinMaxDouble) ||
        (isOptionMatch(selectedScreeningOption.id, MIN_MAX_FLOAT_OPTIONS) &&
          configOptions.hasMinMaxFloat)
      ) {
        criteriaStatements = createComputeScreenItemDataPayload(
          selectedScreeningOption,
          minMaxValues,
          itemType,
          { isInclude: isIncluded },
        )
      } else if (
        isOptionMatch(selectedScreeningOption.id, USER_INPUT_OPTIONS) &&
        configOptions.hasUserInput
      ) {
        criteriaStatements = createComputeScreenItemDataStatement(
          selectedScreeningOption,
          [userInputValue],
          itemType,
          { isInclude: isIncluded },
        )
      }
    }

    try {
      setIsNewScreenCriteriaAdded(true)
      const response = await computeScreenItemData(criteriaStatements)
      if (response.success) {
        onSubmit(criteriaStatements, response.result)
        onClose()
        dispatch(
          showSnackbar({
            message: 'Screen criteria Added successfully',
            severity: 'info',
          }),
        )
      } else {
        throw new Error(response.result || 'Min must not be greater than Max');
        }
    } catch (err: any) {
      setLocalMessage({
        type: 'error',
        text: parseApiError(err),
      });
    } finally {
      setIsNewScreenCriteriaAdded(false)
    }
  }

  // Automatically clear min/max errors when values become valid
  useEffect(() => {
    if (
      localMessage &&
      localMessage.type === 'error' &&
      localMessage.text.toLowerCase().includes('min') &&
      minMaxValues.min !== '' &&
      minMaxValues.max !== '' &&
      Number(minMaxValues.min) <= Number(minMaxValues.max)
    ) {
      setLocalMessage(null);
    }
  }, [localMessage, minMaxValues.min, minMaxValues.max]);

  //keeps user selection state in sync with the user's choice for include/exclude.
  useEffect(() => {
    setSelectedScreenCriteriaState((prev: any) => ({
      ...prev,
      data: {
        ...prev.data,
        isInclude: includeExcludeOption === 'Include' ? true : false,
      },
    }))
  }, [includeExcludeOption])

  useEffect(() => {
    setShowLoader(true)
    
    const now = Date.now()
    const timeSinceLastCall = now - lastDetailsApiCallTime.current
    
    // Prevent duplicate API calls within 1 second (React Strict Mode protection)
    // Skip duplicate prevention in test environment
    if (timeSinceLastCall < 1000 && !(window as any).Cypress) {
      setShowLoader(false)
      return
    }
    
    lastDetailsApiCallTime.current = now
    
    const fetchScreeningItemDetails = async () => {
      try {
        let date = ''
        if (
          selectedScreeningOption.id === ScreeningItemType.PORTFOLIO &&
          selectedPortfolios.length
        ) {
          const portfolioDates = selectedPortfolios.map(
            (portfolioItem) => portfolioItem.date,
          )
          date = dayjs(getLatestDate(portfolioDates)).format(
            API_DATE_TIME_FORMAT,
          )
        }
        const response = await getCandidateScreeningItemDetails(
          selectedScreeningOption.id,
          date,
        )
        setSelectedScreeningItemDetails(response)
      } catch (err) {
        dispatch(
          showSnackbar({
            message: `Error fetching screening item details ${err instanceof Error ? err.message : String(err)}`,
            severity: 'error',
          }),
        )
      } finally {
        setShowLoader(false)
      }
    }
    fetchScreeningItemDetails()
  }, [selectedScreeningOption])

  useEffect(() => {
    setSelectedScreenCriteriaState({
      selectedScreeningItem: selectedScreeningOption,
      data: {
        inputValues: [userInputValue],
        isInclude: includeExcludeOption === 'Include' ? true : false,
      },
      configOptions: selectedScreeningItemDetails.configOptions,
      itemType: selectedScreeningItemDetails.itemType,
    })
  }, [userInputValue])

  const handleUserInputChange = (input: string, maxSize: number) => {
    if (input.length <= maxSize) {
      setUserInputValue(input)
      setUserInputError(false)
    } else {
      setUserInputError(true)
    }
  }

  const renderDynamicContent = () => {
    const config = selectedScreeningItemDetails.configOptions
    const availableValues =
      selectedScreeningItemDetails.availableOptions?.availableValues || []

    if (!config) {
      return null
    }

    if (
      selectedScreeningOption.id === ScreeningItemType.PORTFOLIO &&
      config.hasPortfolioGrid
    ) {
      return (
        <ScreenCriteriaPortfolioGrid
          onSelectPortfolios={handlePortfolioSelect}
          availablePortfolios={
            availableValues as unknown as IScreeningItemPortfolios[]
          }
        />
      )
    }

    if (
      selectedScreeningOption.id === ScreeningItemType.COUP &&
      (config.hasMinMaxFloat ||
        config.hasAllCoupons ||
        config.hasHalfCouponsExcluded ||
        config.hasHalfCouponsIncluded)
    ) {
      return (
        <ConfigSettingUI
          minMaxValues={minMaxValues}
          configSettingType={'couponOption'}
          selectedConfigSetting={selectedConfigSetting}
          configSettingOptions={COUPONS}
          setSelectedConfigSetting={setSelectedConfigSetting}
          onValueChange={handleValueChange}
        />
      )
    }

    if (
      selectedScreeningOption.id === ScreeningItemType.FSTREFD &&
      (config.hasMinMaxDate ||
        config.hasNoSelection ||
        config.hasRefundableSecuritiesOnly ||
        config.hasNonRefundableSecuritiesOnly)
    ) {
      return (
        <ConfigSettingUI
          minMaxValues={minMaxValues}
          configSettingType={'refundingDateOption'}
          selectedConfigSetting={selectedConfigSetting}
          configSettingOptions={REFUNDING_DATE_OPTIONS}
          setSelectedConfigSetting={setSelectedConfigSetting}
          onValueChange={handleValueChange}
        />
      )
    }

    if (
      selectedScreeningOption.id === ScreeningItemType.PUTDATE &&
      (config.hasMinMaxDate ||
        config.hasNoSelection ||
        config.hasPutableSecuritiesOnly ||
        config.hasNonPutableSecuritiesOnly)
    ) {
      return (
        <ConfigSettingUI
          minMaxValues={minMaxValues}
          configSettingType={'putDateOption'}
          selectedConfigSetting={selectedConfigSetting}
          configSettingOptions={PUT_DATE_OPTIONS}
          setSelectedConfigSetting={setSelectedConfigSetting}
          onValueChange={handleValueChange}
        />
      )
    }

    if (
      selectedScreeningOption.id === ScreeningItemType.CALLDATE &&
      (config.hasMinMaxDate ||
        config.hasNoSelection ||
        config.hasCallable ||
        config.hasCallableMakeWhole ||
        config.hasCallableNonMakeWhole ||
        config.hasNonCallable ||
        config.hasNonCallableLife)
    ) {
      return (
        <ConfigSettingUI
          minMaxValues={minMaxValues}
          configSettingType={'callDateOption'}
          selectedConfigSetting={selectedConfigSetting}
          configSettingOptions={CALLDATE_OPTIONS}
          setSelectedConfigSetting={setSelectedConfigSetting}
          onValueChange={handleValueChange}
        />
      )
    }

    if (
      (selectedScreeningOption.id === ScreeningItemType.CURWALA &&
        config.hasMinMaxShort) ||
      (isOptionMatch(selectedScreeningOption.id, MIN_MAX_DOUBLE_OPTIONS) &&
        config.hasMinMaxDouble) ||
      (isOptionMatch(selectedScreeningOption.id, MIN_MAX_FLOAT_OPTIONS) &&
        config.hasMinMaxFloat)
    ) {
      // Use existing error logic to set helper texts
      const minError = minTouched && minMaxValues.min !== '' && minMaxValues.max !== '' && Number(minMaxValues.min) > Number(minMaxValues.max);
      const maxError = maxTouched && minMaxValues.min !== '' && (minMaxValues.max === '' || Number(minMaxValues.max) < Number(minMaxValues.min));
      const minHelperText = minError ? 'Minimum must be ≤ Maximum' : '';
      const maxHelperText = maxError ? 'Maximum must be ≥ Minimum and not empty' : '';
      return (
        <MinMaxInput
          minValue={minMaxValues.min}
          maxValue={minMaxValues.max}
          onValueChange={handleValueChange}
          minLabel='Minimum'
          maxLabel='Maximum'
          minError={minError}
          maxError={maxError}
          minHelperText={minHelperText}
          maxHelperText={maxHelperText}
          onMinBlur={() => setMinTouched(true)}
          onMaxBlur={() => setMaxTouched(true)}
        />
      )
    }

    if (
      isOptionMatch(selectedScreeningOption.id, USER_INPUT_OPTIONS) &&
      config.hasUserInput
    ) {
      return (
        <Box
          display='flex'
          className='user-input-config-box'
          alignItems='center'
          gap={1}
        >
          <Typography>{config.rangeTitle}:</Typography>
          <TextField
            value={userInputValue}
            onChange={(e) =>
              handleUserInputChange(
                e.target.value,
                config.userInputMaxSize as number,
              )
            }
            error={userInputError}
            helperText={
              userInputError
                ? `Max ${config.userInputMaxSize} characters allowed`
                : ''
            }
            size='small'
            variant='outlined'
          />
        </Box>
      )
    }

    if (
      (selectedScreeningOption.id === ScreeningItemType.MEMCODE &&
        config.hasBenchmark) ||
      (isOptionMatch(selectedScreeningOption.id, INFO_STRUCT_OPTIONS) &&
        config.hasInvert)
    ) {
      return (
        <TreeViewList
          items={availableValues}
          onSelectedItems={setSelectedTreeItems}
          searchTerm={searchListTerm}
          title={config.hasBenchmark ? 'Select Benchmark' : 'Select Options'}
        />
      )
    }

    if (config.hasMinMax) {
      return (
        <MinMaxInput
          minValue={minMaxValues.min}
          maxValue={minMaxValues.max}
          onValueChange={handleValueChange}
          minLabel='Minimum'
          maxLabel='Maximum'
        />
      )
    }

    return null
  }

  return (
    <Dialog open={isOpen} onClose={handleDialogClose} fullWidth maxWidth='xl'>
      <DialogTitle>
        {mode === CriteriaOperation.EDIT
          ? CriteriaOperation.EDIT
          : CriteriaOperation.ADD}
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ padding: 2 }}>
        <Paper sx={classes.root}>
          {/* Left Column */}
          <Box sx={classes.leftColumn}>
            <Box sx={classes.header}>
              <Typography variant='h5'>Screening Item</Typography>
              <SearchBar onChange={setSearchTerm} />
            </Box>
            <Divider />
            <ConfigurableList
              options={screeningOptions}
              onSelect={handleListSelect}
              option={selectedScreeningOption}
              searchTerm={searchTerm}
              displayId={true}
              loading={false}
            />
          </Box>

          {/* Right Column */}
          <Box sx={classes.rightColumn}>
            <Box sx={classes.header}>
              <Typography variant='h5'>Range</Typography>

              <Box display={'flex'}>
                {selectedScreeningItemDetails?.configOptions
                  ?.hasIncludeExclude && (
                  <RadioButtons
                    layout='horizontal'
                    value={includeExcludeOption}
                    onChange={(e) =>
                      setIncludeExcludeOption(
                        e.target.value as 'Include' | 'Exclude',
                      )
                    }
                    options={['Include', 'Exclude']}
                  />
                )}
                {selectedScreeningItemDetails?.configOptions?.hasSearch && (
                  <SearchBar onChange={setSearchListTerm} />
                )}
              </Box>
            </Box>
            <Divider />
            {showLoader ? (
              <LoadingCard />
            ) : isConfigOptionsAvailable() ? (
              <Box sx={classes.content}>{renderDynamicContent()}</Box>
            ) : (
              <NoDataToDisplayCard />
            )}
          </Box>
        </Paper>
        {localMessage && localMessage.type === 'error' && (
          <div className="modal-error-message" style={{ color: 'red', marginTop: 16, textAlign: 'center' }}>{localMessage.text}</div>
        )}
      </DialogContent>
      <DialogActions sx={classes.dialogActions}>
        <Button
          variant='contained'
          color='primary'
          onClick={handleSubmit}
          disabled={!selectedScreeningOption}
        >
          Add Screening Criteria
        </Button>
        <Button color='primary' onClick={onClose}>
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default React.memo(CriteriaModal)
